import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import adminService from '../services/admin/admin-authService';

const AdminAuthContext = createContext({});

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check for existing session on mount
  useEffect(() => {
    checkExistingSession();
  }, []);

  const checkExistingSession = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const userStr = localStorage.getItem('adminUser');

      if (token && userStr) {
        // Verify token is still valid by making a profile request
        const response = await adminService.getProfile();
        if (response.success) {
          setAdmin(response.data);
          adminService.setAuthData(token, response.data);
        } else {
          clearSession();
        }
      }
    } catch (error) {
      console.error('Session check error:', error);
      clearSession();
    } finally {
      setLoading(false);
    }
  };

const login = async (email, password) => {
  setLoading(true);
  setError(null);
  
  try {
    const response = await adminService.login(email, password);
    
    if (response.success) {
      const { token, user } = response.data;
      adminService.setAuthData(token, user);
      
      // Fetch fresh permissions from server
      const profileResponse = await adminService.getProfile();
      if (profileResponse.success) {
        setAdmin(profileResponse.data);
        adminService.setAuthData(token, profileResponse.data);
      } else {
        setAdmin(user);
      }
      
      return { success: true, user };
    } else {
      setError(response.message || 'Login failed');
      return { success: false, message: response.message };
    }
  } catch (error) {
    const message = error.message || 'Login failed. Please try again.';
    setError(message);
    return { success: false, message };
  } finally {
    setLoading(false);
  }
};


  const demoLogin = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminService.demoLogin();
      
      if (response.success) {
        const { token, user } = response.data;
        adminService.setAuthData(token, user);
        setAdmin(user);
        return { success: true, user };
      } else {
        setError(response.message || 'Demo login failed');
        return { success: false, message: response.message };
      }
    } catch (error) {
      const message = error.message || 'Demo login failed. Please try again.';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const logout = useCallback(async () => {
    try {
      await adminService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      clearSession();
    }
  }, []);

  const clearSession = () => {
    adminService.clearAuthData();
    setAdmin(null);
    setError(null);
  };

  const updateProfile = async (profileData) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminService.updateProfile(profileData);
      
      if (response.success) {
        setAdmin(response.data);
        
        // Update localStorage
        const currentUser = adminService.getCurrentUser();
        const updatedUser = { ...currentUser, ...response.data };
        localStorage.setItem('adminUser', JSON.stringify(updatedUser));
        
        return { success: true, user: response.data };
      } else {
        setError(response.message);
        return { success: false, message: response.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Profile update failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await adminService.changePassword(currentPassword, newPassword);
      
      if (response.success) {
        return { success: true };
      } else {
        setError(response.message);
        return { success: false, message: response.message };
      }
    } catch (error) {
      const message = error.response?.data?.message || 'Password change failed';
      setError(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const isAuthenticated = () => {
    return !!admin && adminService.isAuthenticated();
  };

  const value = {
    admin,
    loading,
    error,
    setError,
    login,
    demoLogin,
    logout,
    updateProfile,
    changePassword,
    isAuthenticated,
    clearError: () => setError(null),
    clearSession,
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};